import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play } from 'lucide-react';
import { FaYoutube, FaInstagram } from 'react-icons/fa';
import { useContent } from '@/lib/content';
import { resolveMediaSrc, thumbSrc } from '@/lib/api';
import { parseVideoUrl, ytThumb } from '@/lib/video';
import { LazyImage } from '@/components/LazyImage';
import { SectionHeading, Reveal } from '@/components/Reveal';
import { Lightbox } from '@/components/Lightbox';
import { trackEvent } from '@/lib/analytics';

const PortfolioCard = ({ item, onOpen, index }) => {
  const video = item.type === 'video' ? parseVideoUrl(item.videoUrl) : null;
  const thumb = item.type === 'image' ? item.image : item.image || (video?.platform === 'youtube' && video.id ? ytThumb(video.id) : '');

  const motionProps = {
    layout: true,
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, scale: 0.97 },
    transition: { duration: 0.5, delay: (index % 6) * 0.05 },
    'data-testid': `portfolio-item-${item.id}`,
  };

  const strip = (
    <div className="p-4 sm:p-5 h-[118px] shrink-0 flex flex-col">
      <p className="text-[10px] uppercase tracking-[0.3em] text-champagne truncate">{item.category}{item.location ? ` — ${item.location}` : ''}</p>
      <p className="font-serif text-lg sm:text-xl text-cream mt-1 leading-tight truncate">{item.title}</p>
      <p className="text-white/50 text-sm font-light mt-1.5 leading-relaxed truncate">{(item.caption || item.description) || ''}</p>
    </div>
  );

  return (
    <motion.button
      {...motionProps}
      onClick={() => onOpen(item)}
      className="group relative block w-full h-full text-left border border-white/10 bg-surface overflow-hidden flex flex-col"
    >
      <div className="relative w-full aspect-[4/5] overflow-hidden bg-ink shrink-0">
        {thumb ? (
          <LazyImage
            src={thumbSrc(thumb)}
            alt={`${item.title} — ${item.category} event in ${item.location || 'Hyderabad'} by Events by AJ`}
            wrapperClassName="absolute inset-0"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            placeholder={item.blur}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span className="text-champagne">
              {video?.platform === 'instagram' ? <FaInstagram size={30} /> : <FaYoutube size={30} />}
            </span>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40">
              {video?.platform === 'instagram' ? 'Instagram' : 'Video'}
            </p>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
        {item.type === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full border border-champagne/70 bg-ink/40 backdrop-blur-sm flex items-center justify-center text-champagne group-hover:scale-110 transition-transform duration-300">
              <Play size={20} fill="currentColor" />
            </span>
          </div>
        )}
        {video && (
          <span className="absolute top-3 right-3 text-white/80 bg-ink/50 backdrop-blur-sm p-2">
            {video.platform === 'youtube' ? <FaYoutube size={16} /> : <FaInstagram size={16} />}
          </span>
        )}
      </div>
      {strip}
    </motion.button>
  );
};

const slugifyLabel = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-');

export const Portfolio = ({ initialFilter, teaser = false }) => {
  const { portfolio, categories, loaded } = useContent();
  const listedCats = useMemo(() => categories.filter((c) => c.listed !== false), [categories]);
  const [filter, setFilter] = useState('');
  const [activeIndex, setActiveIndex] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);

  useEffect(() => { setVisibleCount(12); }, [filter]);

  useEffect(() => {
    if (listedCats.length === 0) return;
    if (initialFilter) {
      const match = categories.find((c) => c.slug === initialFilter || slugifyLabel(c.label) === initialFilter);
      if (match) {
        setFilter(match.label);
        return;
      }
    }
    setFilter((prev) => (prev && listedCats.some((c) => c.label === prev) ? prev : listedCats[0].label));
  }, [initialFilter, categories, listedCats]);

  const items = useMemo(
    () => portfolio.filter((i) => i.category === filter),
    [filter, portfolio]
  );

  const shown = teaser ? items.slice(0, 6) : items.slice(0, visibleCount);
  const activeCat = listedCats.find((c) => c.label === filter);

  const open = (item) => {
    trackEvent('portfolio_open', { item: item.id, category: item.category });
    setActiveIndex(shown.findIndex((i) => i.id === item.id));
  };

  const selectFilter = (c) => {
    setFilter(c.label);
    trackEvent('portfolio_filter', { category: c.label });
    window.history.pushState({}, '', `/portfolio/${c.slug || slugifyLabel(c.label)}`);
  };

  return (
    <section id="portfolio" data-testid="portfolio-section" className="py-24 sm:py-32 bg-surface border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHeading chapter="04" label="Our Work" title={<>Moments we&rsquo;ve <span className="italic text-champagne">brought to life</span></>} />

        {!teaser && (
        <Reveal className="mt-12 -mx-5 px-5 sm:mx-0 sm:px-0 overflow-x-auto" y={20}>
          <div className="flex gap-2 sm:flex-wrap pb-2" data-testid="portfolio-filters">
            {listedCats.map((c) => (
              <button
                key={c.label}
                data-testid={`filter-${c.slug || slugifyLabel(c.label)}`}
                onClick={() => selectFilter(c)}
                className={`shrink-0 px-4 py-2 text-[11px] uppercase tracking-[0.25em] border transition-colors duration-300 ${
                  filter === c.label ? 'border-champagne text-champagne' : 'border-white/15 text-white/50 hover:border-white/40 hover:text-cream'
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </Reveal>
        )}

        {!loaded && (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-loading">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-surface border border-white/5 animate-pulse aspect-[4/5]" />
            ))}
          </div>
        )}

        {loaded && (
        <motion.div layout className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-grid">
          <AnimatePresence mode="popLayout">
            {shown.map((item, i) => (
              <PortfolioCard key={item.id} item={item} index={i} onOpen={open} />
            ))}
          </AnimatePresence>
        </motion.div>
        )}

        {!teaser && loaded && items.length > visibleCount && (
          <div className="mt-12 text-center">
            <button
              data-testid="portfolio-load-more"
              onClick={() => { setVisibleCount(visibleCount + 12); trackEvent('portfolio_load_more', { category: filter }); }}
              className="btn-outline"
            >
              Load More ({items.length - visibleCount} remaining)
            </button>
          </div>
        )}

        {teaser && loaded && activeCat && (
          <div className="mt-12 text-center">
            <a
              data-testid="portfolio-see-all"
              href={`/portfolio/${activeCat.slug || slugifyLabel(activeCat.label)}`}
              className="btn-gold"
            >
              See All {activeCat.label}
            </a>
          </div>
        )}

        {!teaser && loaded && items.length === 0 && (
          <p className="mt-16 text-center text-white/40 font-light">Stories from this category are coming soon.</p>
        )}
      </div>

      {activeIndex !== null && items[activeIndex] && (
        <Lightbox
          item={items[activeIndex]}
          onClose={() => setActiveIndex(null)}
          onPrev={() => setActiveIndex((activeIndex - 1 + items.length) % items.length)}
          onNext={() => setActiveIndex((activeIndex + 1) % items.length)}
        />
      )}
    </section>
  );
};
